import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, ShieldCheck, Sparkles } from "lucide-react";
import { SRETRADE_REGISTER } from "../mock";

const HERO_IMG =
  "https://images.unsplash.com/photo-1560221328-12fe60f83ab8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const HeroSlider = () => {
  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ minHeight: "560px" }}
    >
      {/* Background image */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url('${HERO_IMG}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {/* Gradient overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(105deg, rgba(15,58,130,0.94) 0%, rgba(28,77,156,0.85) 40%, rgba(28,77,156,0.55) 70%, rgba(15,58,130,0.35) 100%)",
        }}
      />
      {/* Decorative dots */}
      <div
        className="absolute inset-0 opacity-15"
        style={{
          backgroundImage:
            "radial-gradient(circle at 80% 20%, white 1.5px, transparent 1.5px)",
          backgroundSize: "44px 44px",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-4 py-20 md:py-24 grid lg:grid-cols-[1.1fr,1fr] gap-12 items-center">
        {/* Left content */}
        <div className="text-white">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-[12px] tracking-[0.16em] font-semibold mb-6 border border-white/20">
            <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
            YENİ YATIRIM FIRSATLARI
          </span>
          <h1 className="text-4xl md:text-[54px] font-bold leading-[1.08] mb-6">
            Geleceğinize{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-yellow-300">
                bugün
              </span>
              <span className="absolute left-0 right-0 bottom-1 h-2 bg-yellow-300/30 rounded-full -z-0" />
            </span>{" "}
            yatırım yapın
          </h1>
          <p className="text-white/90 text-lg leading-relaxed mb-8 max-w-xl">
            37 yıllık tecrübemiz, uzman analiz kadromuz ve güçlü işlem
            platformlarımızla Borsa İstanbul ve VİOP piyasalarında{" "}
            <span className="font-semibold text-yellow-200">
              kazanç fırsatlarını
            </span>{" "}
            kaçırmayın.
          </p>

          <div className="flex flex-wrap gap-3 mb-10">
            <a
              href={SRETRADE_REGISTER}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-[#e23a3a] hover:bg-[#c52d2d] text-white px-7 py-3.5 rounded-md font-semibold transition shadow-lg shadow-red-500/20"
            >
              Hemen Hesap Aç
              <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/hizmetlerimiz/viop"
              className="inline-flex items-center gap-2 border-2 border-white/70 hover:bg-white hover:text-[#1c4d9c] text-white px-6 py-3 rounded-md font-semibold transition"
            >
              Fırsatları Keşfet
            </Link>
          </div>

          {/* Trust stats */}
          <div className="grid grid-cols-3 gap-3 max-w-md">
            {[
              { v: "37+", l: "Yıl Tecrübe" },
              { v: "50K+", l: "Yatırımcı" },
              { v: "%0.08", l: "Komisyon" },
            ].map((s, i) => (
              <div
                key={i}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/15"
              >
                <div className="text-2xl font-bold text-white">{s.v}</div>
                <div className="text-[11px] text-white/70 mt-0.5 uppercase tracking-wider">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: opportunity card */}
        <div className="hidden lg:block">
          <OpportunityCard />
        </div>
      </div>
    </section>
  );
};

const movers = [
  { sym: "BIST 100", val: "10,142.85", chg: "+1.24%", up: true },
  { sym: "GARAN", val: "94.20", chg: "+2.15%", up: true },
  { sym: "ASELS", val: "68.10", chg: "+0.92%", up: true },
  { sym: "KCHOL", val: "185.40", chg: "+0.75%", up: true },
  { sym: "ALTIN GR", val: "2,684", chg: "+0.42%", up: true },
];

const OpportunityCard = () => (
  <div className="relative">
    {/* Glow */}
    <div
      className="absolute -inset-6 rounded-3xl blur-2xl opacity-40"
      style={{
        background:
          "radial-gradient(circle, rgba(254,240,138,0.45) 0%, transparent 70%)",
      }}
    />
    <div className="relative bg-white/95 backdrop-blur-md rounded-2xl p-6 shadow-2xl border border-white/30">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[11px] tracking-wider text-slate-500 font-semibold">
            CANLI PİYASA
          </div>
          <div className="text-lg font-bold text-slate-900 flex items-center gap-2">
            En Çok Yükselenler
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          </div>
        </div>
        <div className="px-2.5 py-1 rounded bg-emerald-100 text-emerald-700 text-[11px] font-bold flex items-center gap-1">
          <TrendingUp className="w-3 h-3" /> BULL
        </div>
      </div>

      {/* Big chart */}
      <div className="relative h-32 mb-5 rounded-lg bg-gradient-to-br from-emerald-50 to-white overflow-hidden border border-emerald-100">
        <svg viewBox="0 0 320 120" className="w-full h-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
            </linearGradient>
          </defs>
          <polyline
            points="0,95 30,80 60,85 90,60 120,68 150,45 180,52 210,30 240,40 270,18 300,25 320,10"
            fill="none"
            stroke="#10b981"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <polyline
            points="0,95 30,80 60,85 90,60 120,68 150,45 180,52 210,30 240,40 270,18 300,25 320,10 320,120 0,120"
            fill="url(#g1)"
            stroke="none"
          />
          <circle cx="320" cy="10" r="4" fill="#10b981" />
          <circle cx="320" cy="10" r="9" fill="#10b981" fillOpacity="0.25" />
        </svg>
        <div className="absolute top-3 left-3">
          <div className="text-[11px] text-slate-500">Bugünkü Performans</div>
          <div className="text-2xl font-bold text-emerald-600">+%1.24</div>
        </div>
      </div>

      {/* Movers list */}
      <div className="space-y-2">
        {movers.map((m, i) => (
          <div
            key={i}
            className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50 transition"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-md bg-[#1c4d9c]/10 text-[#1c4d9c] flex items-center justify-center text-[11px] font-bold">
                {m.sym.slice(0, 2)}
              </div>
              <div>
                <div className="text-[13px] font-semibold text-slate-800">
                  {m.sym}
                </div>
                <div className="text-[11px] text-slate-500">BIST</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[13px] font-semibold text-slate-800">
                {m.val}
              </div>
              <div className="text-[11px] text-emerald-600 font-bold flex items-center justify-end gap-0.5">
                <TrendingUp className="w-3 h-3" /> {m.chg}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer note */}
      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-2 text-[11px] text-slate-500">
        <ShieldCheck className="w-3.5 h-3.5 text-[#1c4d9c]" />
        SPK lisanslı aracı kurum · Veriler 15dk gecikmelidir
      </div>
    </div>
  </div>
);

export default HeroSlider;
